import { Container, DisplayObject } from '@tencent/alloy-renderer';
import PlayerRenderArea from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/layered-render/plalyer-state-layered/player-render-area';
import { IAnimTarget } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-interface';
import { LayerType,  AllowLayer } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-animations/element-animation/anim-constants';
import { ILayerService, IRenderConfig, IEleModel } from './player-layer-interface';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';

export default class PlayerLayerService implements ILayerService {
    public static instance: PlayerLayerService;
    public static getInstance(): PlayerLayerService {
        if (!this.instance) {
            this.instance = new PlayerLayerService();
        }
        return this.instance;
    }
    // 当前元素层（最底层canvas）
    public baseLayer?: Container | null; // 当前挂载的canvas画布layers
    // 当前页有无可点击元素映射map
    public slideHasClickEleMap: Map<string, boolean> = new Map();
    // 操作层canvas渲染树
    public operationLayer: PlayerRenderArea | null;
    // 覆盖层canvas渲染树
    private overlayLayer: PlayerRenderArea | null;
    // 操作层元素
    private operationElements: Array<DisplayObject> = [];
    // 覆盖层元素
    private overlayElements: Array<DisplayObject> = [];
    // 当前元素层（最底层canvas）正在播放的动画
    private activeBaseLayerAnims: Array<string> = [];
    // 操作层（中间层canvas）正在播放的动画
    private activeOptLayerAnims: Array<string> = [];

    /**
     * 当前元素层（最底层canvas）是否有正在运行的动画
     */
    public get hasActiveAnimInBaseLayer(): boolean {
        return !!this.activeBaseLayerAnims.length;
    }

    /**
     * 操作层（中间层canvas)是否有正在运行的动画
     */
    public get hasActiveAnimInOptLayer(): boolean {
        return !!this.activeOptLayerAnims.length;
    }

    /**
     * 操作层和覆盖层进行初始化，创建canvas渲染树
     * @param config 宽、高、缩放比例
     * @param operationCanvasRef 操作层 canvas dom元素
     * @param overlayCanvasRef 覆盖层 canvas dom元素
     */
    public initLayer(
        config: IRenderConfig,
        operationCanvasWrapperRef: HTMLDivElement,
        overlayCanvasWrapperRef: HTMLDivElement,
    ): void {
        this.operationLayer = new PlayerRenderArea({
            ...config,
            canvasWrapper: operationCanvasWrapperRef,
            autoRender: false, // 操作层有动画，不允许自动渲染
        });
        this.overlayLayer = new PlayerRenderArea({
            ...config,
            canvasWrapper: overlayCanvasWrapperRef,
            autoRender: true, // 覆盖层无动画，允许自动渲染
        });
    }

    /**
     * 清空元素，销毁各层
     */
    public dispose(): void {
        this.operationLayer?.disposeApplication();
        this.overlayLayer?.disposeApplication();
        this.operationLayer = null;
        this.overlayLayer = null;
        this.baseLayer = null;
        this.operationElements = [];
        this.overlayElements = [];
        this.slideHasClickEleMap = new Map();
    }

    /**
     * 更新宽、高、缩放比例
     * @param config 宽、高、缩放比例
     */
    public updateLayersRect(config: IRenderConfig): void {
        this.operationLayer?.updateRenderRect(config);
        this.overlayLayer?.updateRenderRect(config);
    }

    /**
     * 对操作元素进行分层
     * @param animTargetModel 被操作的动画目标元素
     * 四种情况：初次分层，操作元素在当前元素层、操作元素在操作层、操作元素在覆盖层
     */
    public processSlice(animTargetModel: IElementBaseModel): void {
        if (!this.baseLayer) return;
        // 需要操作的元素，只会在当前元素层、操作层、覆盖层中的一层出现，不会同时在多层出现
        const { baseLayer, overlayElements, operationElements } = this;
        const func = child => child.factoryOptions.model === animTargetModel;
        const baseIndex = baseLayer.children.findIndex(func);
        // Case1 第一次进行分层
        if (!operationElements.length && !overlayElements.length) {
            this.executeFirstLayer(baseLayer, baseIndex);
            return;
        }

        // Case2 操作元素在当前元素层，则需要把当前元素层的该元素及之后的元素，挪到操作层，覆盖层不用动
        if (baseIndex !== -1) {
            this.moveBaseToOperation(
                baseLayer,
                baseLayer?.children.length - baseIndex,
            );
            return;
        }

        // Case3 操作元素已在操作层，则保持现状，不做处理
        const operationIndex = operationElements.findIndex(func);
        if (operationIndex !== -1) return;


        // Case4 操作元素在覆盖层，在需把覆盖层的该元素及其之前的元素，由覆盖层挪入操作层，当前层不用动
        const overlayIndex = overlayElements.findIndex(func);
        if (overlayIndex !== -1) {
            this.moveOverlayToOperation(overlayIndex);
            return;
        }
    }

    /**
     * 动画结束后，操作层元素已完成操作，需从操作层移出，并移入当前元素层
     * @param animTargetModel 动画目标元素
     */
    public removeOperationToBaseLayer(animTargetModel: IElementBaseModel): void {
        if (!AllowLayer || !this.baseLayer || !this.operationElements.length) return;
        const func = child => child.factoryOptions.model === animTargetModel;

        const operationIndex = this.operationElements.findIndex(func);
        if (operationIndex === -1) {
            console.error('animation target is not in operation layer when complete');
            return;
        }
        this.moveOperationToBase(operationIndex);
    }

    /**
     * 所有元素都恢复到当前元素层(最底层canvas)进行渲染
     */
    public restoreToBaseLayer() {
        if (!AllowLayer || !this.baseLayer) return;
        const { baseLayer } = this;
        this.operationElements.forEach((comp) => {
            baseLayer.addChild(comp);
        });
        this.overlayElements.forEach((comp) => {
            baseLayer.addChild(comp);
        });

        this.operationLayer?.clearRenderComponent();
        this.overlayLayer?.clearRenderComponent();
        this.operationElements = [];
        this.overlayElements = [];

        this.baseLayerRender();
        this.optLayerRender();
    }

    /**
     * 存储在当前元素层（最底层canvas）运行的动画目标元素key
     * @param animTargetKey 动画目标元素key
     */
    public addActiveAnimsKey(animTargetKey: string, type: LayerType): void {
        // animTargetKey可以重复push，因为一个元素上可以挂多个动画
        const animsMap = {
            [LayerType.BASE]: this.activeBaseLayerAnims,
            [LayerType.OPERATION]: this.activeOptLayerAnims,
        };
        animsMap[type].push(animTargetKey);
    }

    /**
     * 当前元素层的动画元素播放完毕后，移出当前元素层
     * @param animTargetKey 动画目标元素key
     */
    public removeActiveAnimsKey(animTargetKey: string): void {
        const baseIndex = this.activeBaseLayerAnims.indexOf(animTargetKey);
        const optIndex = this.activeOptLayerAnims.indexOf(animTargetKey);

        if (baseIndex !== -1) {
            this.activeBaseLayerAnims.splice(baseIndex, 1);
        }

        if (optIndex !== -1) {
            this.activeOptLayerAnims.splice(optIndex, 1);
        }
    }

    /**
     * 在操作层（动画运行层）查找动画目标元素
     * @param targetModelKey 动画目标元素key
     * @returns DisplayObject
     */
    public findAnimTargetInOperationLayer(targetModelKey: string): DisplayObject | undefined {
        return this.operationLayer?.findAnimTarget(targetModelKey);
    }

    /**
     * 在覆盖层查找动画目标元素
     * @param targetModelKey 动画目标元素key
     * @returns DisplayObject
     */
    public findAnimTargetInOverlayLayer(targetModelKey: string): DisplayObject | undefined {
        return this.overlayLayer?.findAnimTarget(targetModelKey);
    }

    /**
     * 初次分层，操作元素的上层元素全部由当前层移入覆盖层，操作元素由当前层移入操作层
     * @param baseLayer 当前元素层（即未分层前的渲染层）
     * @param baseIndex 操作元素在当前层的位置
     */
    private executeFirstLayer(baseLayer: Container, baseIndex: number): void {
        // 取覆盖层数据，将操作元素的上级元素移动到覆盖层canvas
        this.moveBaseToOverlay(
            baseLayer,
            baseLayer?.children.length - baseIndex - 1,
        );
        // 取操作层数据，移动到操作层canvas
        this.moveBaseToOperation(baseLayer, 1);
    }

    /**
     * 将当前层元素移入操作层
     * @param baseLayer 当前元素层
     * @param number 需要移入操作层的元素的数量
     */
    private moveBaseToOperation(baseLayer: Container, number: number): void {
        let operationElementNumber = number;
        while (operationElementNumber > 0) {
            const child = baseLayer?.children[baseLayer.children.length - 1];
            if (!child) return;
            // 当前元素层一旦开始动画，则不允许自动渲染，需手动调用
            baseLayer?.removeChild(child);
            // 操作层创建时，不允许自动渲染，需手动调用
            this.operationLayer?.prependChild(child);

            this.operationElements.unshift(child);
            operationElementNumber -= 1;
        }
        this.baseLayerRender();
        this.optLayerRender();
    }

    /**
     * 将元素由当前层移入覆盖层
     * @param baseLayer 当前元素层
     * @param number 需移入覆盖层的元素的数量
     */
    private moveBaseToOverlay(baseLayer: Container, number: number): void {
        let overlayElementNumber = number;
        while (overlayElementNumber > 0) {
            const child = baseLayer?.children[baseLayer.children.length - 1];
            if (!child) return;
            baseLayer?.removeChild(child);
            this.overlayLayer?.prependChild(child);

            this.overlayElements.unshift(child);
            overlayElementNumber -= 1;
        }

        this.baseLayerRender();
    }

    /**
     * 将元素由覆盖层移入操作层
     * @param overlayIndex 操作元素在覆盖层的位置
     */
    private moveOverlayToOperation(overlayIndex: number): void {
        // 若动画元素在覆盖层，则需要把覆盖层中的动画元素及其之前的元素，挪到操作层，当前元素层不用动
        let operationNumber = 0;
        while (operationNumber <= overlayIndex) {
            // 覆盖层移出，操作层移入
            const [firstChild] = this.overlayElements;
            if (!firstChild) return;
            // 覆盖层autoRender为true，不必手动调
            this.overlayLayer?.removeChild(firstChild);

            this.operationLayer?.appendChild(firstChild);

            this.overlayElements.shift();
            this.operationElements.push(firstChild);
            operationNumber += 1;
        }
        this.optLayerRender();
    }

    /**
     * 将元素由操作层移入当前元素层
     * @param canMoveIndex 操作元素在操作层的位置
     */
    private moveOperationToBase(operationIndex: number): void {
        const canMoveIndex = this.calcCanMoveIndex(operationIndex);
        if (canMoveIndex === -1) {
            console.log('can not move');
            return;
        }
        let operationNumber = 0;
        while (operationNumber <= canMoveIndex) {
            // 操作层移出，当前元素层移入
            const [firstChild] = this.operationElements;
            if (!firstChild) return;
            this.operationLayer?.removeChild(firstChild);
            this.baseLayer?.addChild(firstChild);
            this.operationElements.shift();
            operationNumber += 1;
        }

        this.optLayerRender();
        this.baseLayerRender(true);
    }

    /**
     * 计算从操作层移入当前元素层的操作层元素的最大位置
     * @param operationIndex 操作元素在操作层的位置
     * @returns number 操作层中可以移入当前层的元素的最大位置
     * operationIndex及之前的元素可能有动画，只将动画已经结束的元素从操作层移入当前元素层
     */
    private calcCanMoveIndex(operationIndex: number): number {
        let canMoveIndex = -1;
        for (let i = 0; i <= operationIndex; i++) {
            const childCom = this.operationElements[i] as IAnimTarget;
            const { animList, key } = childCom.factoryOptions.model;
            if (animList?.length && this.activeOptLayerAnims.includes(key)) {
                break;
            }
            canMoveIndex = i;
        }

        return canMoveIndex;
    }

    /**
     * 操作层canvas树进行渲染
     */
    private optLayerRender(sync?: boolean): void {
        this.appRender(this.operationLayer?.getApplication(), sync);
    }

    /**
     * 当前元素层canvas树进行渲染
     */
    private baseLayerRender(sync?: boolean): void {
        this.appRender(this.baseLayer?.app, sync);
    }

    /**
     * canvas树进行同步渲染或者下一帧渲染
     * @param app canvas树所在的application
     * @param sync 是否同步渲染，默认false在下一个动画帧进行渲染
     */
    private appRender(app, sync?: boolean): void {
        sync ? app?.render() :  app?.requestRender();
    }
}
